// App entry point - React lazy loading for code splitting (cache v2)
import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { ScrollToTop } from "@/components/ScrollToTop";
import { AuthProvider } from "@/contexts/AuthContext";
import { FreeAccessProvider } from "@/contexts/FreeAccessContext";
import { StoreProvider, useStore } from "@/contexts/StoreContext";
import { SyncProvider } from "@/contexts/SyncContext";
import { ChatProvider } from "@/contexts/ChatContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminProtectedRoute } from "@/components/auth/AdminProtectedRoute";
import { ChatDrawer } from "@/components/chat/ChatDrawer";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ImpersonationBanner } from "@/components/admin/ImpersonationBanner";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import Maintenance from "./pages/Maintenance";

// Marketing pages - Index is loaded eagerly for LCP
import Index from "./pages/Index";

// Lazy-loaded marketing pages
const Features = lazy(() => import("./pages/Features"));
const Pricing = lazy(() => import("./pages/Pricing"));
const FAQ = lazy(() => import("./pages/FAQ"));
const HowItWorks = lazy(() => import("./pages/HowItWorks"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy-loaded auth pages
const Login = lazy(() => import("./pages/auth/Login"));
const Signup = lazy(() => import("./pages/auth/Signup"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const VerifyEmail = lazy(() => import("./pages/auth/VerifyEmail"));

// Lazy-loaded app pages
const Dashboard = lazy(() => import("./pages/app/Dashboard"));
const Products = lazy(() => import("./pages/app/Products"));
const Orders = lazy(() => import("./pages/app/Orders"));
const SEO = lazy(() => import("./pages/app/SEO"));
const Chat = lazy(() => import("./pages/app/Chat"));
const ConnectStore = lazy(() => import("./pages/app/ConnectStore"));
const Stores = lazy(() => import("./pages/app/Stores"));
const Analytics = lazy(() => import("./pages/app/Analytics"));
const Settings = lazy(() => import("./pages/app/Settings"));
const Categories = lazy(() => import("./pages/app/Categories"));
const Coupons = lazy(() => import("./pages/app/Coupons"));
const StoreHealth = lazy(() => import("./pages/app/StoreHealth"));
const Reviews = lazy(() => import("./pages/app/Reviews"));
const Menus = lazy(() => import("./pages/app/Menus"));
const MediaLibrary = lazy(() => import("./pages/app/MediaLibrary"));
const CartRecovery = lazy(() => import("./pages/app/CartRecovery"));
const Campaigns = lazy(() => import("./pages/app/Campaigns"));
const ScheduledSales = lazy(() => import("./pages/app/ScheduledSales"));

// Lazy-loaded admin pages
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminStores = lazy(() => import("./pages/admin/AdminStores"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminLogs = lazy(() => import("./pages/admin/AdminLogs"));
const AdminChats = lazy(() => import("./pages/admin/AdminChats"));
const AdminFeedback = lazy(() => import("./pages/admin/AdminFeedback"));
const SettingsLimits = lazy(() => import("./pages/admin/SettingsLimits"));
const Revenue = lazy(() => import("./pages/admin/Revenue"));
const SystemHealth = lazy(() => import("./pages/admin/SystemHealth"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 minutes default
      gcTime: 5 * 60 * 1000, // 5 minutes garbage collection
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Loading fallback for lazy-loaded components
function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
    </div>
  );
}

function AppContent() {
  return (
    <Suspense fallback={<PageLoader />}>
      <MaintenanceGate>
        <Routes>
          {/* Marketing pages */}
          <Route path="/" element={<Index />} />
          <Route path="/features" element={<Features />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />

          {/* Auth pages */}
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />

          {/* App pages (protected) - with ChatDrawer */}
          <Route path="/dashboard" element={<ProtectedRoute><ErrorBoundary><Dashboard /><ChatDrawer /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/products" element={<ProtectedRoute><ErrorBoundary><Products /><ChatDrawer /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/orders" element={<ProtectedRoute><ErrorBoundary><Orders /><ChatDrawer /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/seo" element={<ProtectedRoute><ErrorBoundary><SEO /><ChatDrawer /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/categories" element={<ProtectedRoute><ErrorBoundary><Categories /><ChatDrawer /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/menus" element={<ProtectedRoute><ErrorBoundary><Menus /><ChatDrawer /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/media-library" element={<ProtectedRoute><ErrorBoundary><MediaLibrary /><ChatDrawer /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/coupons" element={<ProtectedRoute><ErrorBoundary><Coupons /><ChatDrawer /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/chat" element={<ProtectedRoute><ErrorBoundary><Chat /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/stores" element={<ProtectedRoute><ErrorBoundary><Stores /><ChatDrawer /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><ErrorBoundary><Analytics /><ChatDrawer /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/store-audit" element={<ProtectedRoute><ErrorBoundary><StoreHealth /><ChatDrawer /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/store-health" element={<ProtectedRoute><ErrorBoundary><StoreHealth /><ChatDrawer /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/reviews" element={<ProtectedRoute><ErrorBoundary><Reviews /><ChatDrawer /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/cart-recovery" element={<ProtectedRoute><ErrorBoundary><CartRecovery /><ChatDrawer /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/campaigns" element={<ProtectedRoute><ErrorBoundary><Campaigns /><ChatDrawer /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/scheduled-sales" element={<ProtectedRoute><ErrorBoundary><ScheduledSales /><ChatDrawer /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><ErrorBoundary><Settings /><ChatDrawer /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/settings/*" element={<ProtectedRoute><ErrorBoundary><Settings /><ChatDrawer /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/connect-store" element={<ProtectedRoute><ErrorBoundary><ConnectStore /><ChatDrawer /></ErrorBoundary></ProtectedRoute>} />

          {/* Admin pages (admin only) */}
          <Route path="/admin" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
          <Route path="/admin/revenue" element={<AdminProtectedRoute><Revenue /></AdminProtectedRoute>} />
          <Route path="/admin/system-health" element={<AdminProtectedRoute><SystemHealth /></AdminProtectedRoute>} />
          <Route path="/admin/users" element={<AdminProtectedRoute><AdminUsers /></AdminProtectedRoute>} />
          <Route path="/admin/stores" element={<AdminProtectedRoute><AdminStores /></AdminProtectedRoute>} />
          <Route path="/admin/analytics" element={<AdminProtectedRoute><AdminAnalytics /></AdminProtectedRoute>} />
          <Route path="/admin/settings" element={<AdminProtectedRoute><AdminSettings /></AdminProtectedRoute>} />
          <Route path="/admin/settings/limits" element={<AdminProtectedRoute><SettingsLimits /></AdminProtectedRoute>} />
          <Route path="/admin/logs" element={<AdminProtectedRoute><AdminLogs /></AdminProtectedRoute>} />
          <Route path="/admin/chats" element={<AdminProtectedRoute><AdminChats /></AdminProtectedRoute>} />
          <Route path="/admin/feedback" element={<AdminProtectedRoute><AdminFeedback /></AdminProtectedRoute>} />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </MaintenanceGate>
    </Suspense>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <FreeAccessProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AuthProvider>
          <StoreProvider>
            <SyncProvider>
              <StoreAwareChatProvider>
                <TooltipProvider>
                  <Toaster />
                  <Sonner />
                  <ImpersonationBanner />
                  <AppContent />
                </TooltipProvider>
              </StoreAwareChatProvider>
            </SyncProvider>
          </StoreProvider>
        </AuthProvider>
      </BrowserRouter>
    </FreeAccessProvider>
  </QueryClientProvider>
);

// Wrapper to pass store info to ChatProvider
function StoreAwareChatProvider({ children }: { children: React.ReactNode }) {
  const { stores } = useStore();
  return <ChatProvider hasStores={stores.length > 0}>{children}</ChatProvider>;
}

type MaintenanceSettings = {
  platform_name: string;
  support_email: string;
  maintenance_mode: boolean;
  maintenance_message: string;
};

const MAINTENANCE_DEFAULTS: MaintenanceSettings = {
  platform_name: "ProteasAI",
  support_email: "support@proteasai.com",
  maintenance_mode: false,
  maintenance_message: "We're currently performing scheduled maintenance. Please check back soon.",
};

function MaintenanceGate({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { data: settings, isLoading } = useQuery({
    queryKey: ["platform-settings", "maintenance"],
    queryFn: async (): Promise<MaintenanceSettings> => {
      const { data, error } = await supabase
        .from("platform_settings")
        .select("key, value, category")
        .eq("category", "general")
        .in("key", ["platform_name", "support_email", "maintenance_mode", "maintenance_message"]);

      if (error || !data) {
        if (error) {
          console.error("Failed to fetch maintenance settings:", error);
        }
        return MAINTENANCE_DEFAULTS;
      }

      const merged = { ...MAINTENANCE_DEFAULTS };
      data.forEach((setting) => {
        if (setting.key === "platform_name") {
          merged.platform_name = String(setting.value);
        } else if (setting.key === "support_email") {
          merged.support_email = String(setting.value);
        } else if (setting.key === "maintenance_mode") {
          merged.maintenance_mode = setting.value === true || setting.value === "true";
        } else if (setting.key === "maintenance_message") {
          merged.maintenance_message = String(setting.value);
        }
      });

      return merged;
    },
    staleTime: 60 * 1000,
  });

  const previewMode = new URLSearchParams(location.search).has("maintenancePreview");
  const allowList = ["/admin", "/login", "/signup", "/forgot-password", "/verify-email"];
  const isAllowedPath = allowList.some((path) => location.pathname.startsWith(path));

  if (isLoading) {
    return <PageLoader />;
  }

  if ((settings?.maintenance_mode || previewMode) && !isAllowedPath) {
    return (
      <Maintenance
        platformName={settings?.platform_name ?? MAINTENANCE_DEFAULTS.platform_name}
        supportEmail={settings?.support_email ?? MAINTENANCE_DEFAULTS.support_email}
        maintenanceMessage={settings?.maintenance_message ?? MAINTENANCE_DEFAULTS.maintenance_message}
      />
    );
  }

  return <>{children}</>;
}

export default App;
